import * as api from "./client.js";

// ============================================================================
// Type Definitions
// ============================================================================

export type JobType =
  | "mysql-to-sqlite"
  | "sqlite-to-postgres"
  | "sales-order-sync"
  | "testimonials-migration";
export type JobStatus = "pending" | "running" | "completed" | "failed";
export type TriggerType = "manual" | "scheduled";

export type JobRun = {
  id: string;
  job_type: JobType;
  trigger_type: TriggerType;
  triggered_by: string | null;
  status: JobStatus;
  started_at: string | null;
  completed_at: string | null;
  logs?: string;
  error_message: string | null;
  result_summary: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
};

export type JobRunsPagination = {
  total: number;
  limit: number;
  offset: number;
  has_more: boolean;
};

export type JobRunsListResponse = {
  runs: JobRun[];
  pagination: JobRunsPagination;
  request_id: string;
};

export type JobRunDetailResponse = {
  run: JobRun;
  request_id: string;
};

export type TriggerJobResponse = {
  run: JobRun;
  message: string;
  request_id: string;
};

export type JobRunsQueryParams = {
  jobType: JobType;
  status?: JobStatus;
  limit?: number;
  offset?: number;
};

// ============================================================================
// Service
// ============================================================================

export const syncersService = {
  /**
   * Get all job runs with optional filtering
   */
  async getJobRuns(params: JobRunsQueryParams): Promise<JobRunsListResponse> {
    const queryParams = new URLSearchParams();

    queryParams.append("jobType", params.jobType);
    if (params.status) queryParams.append("status", params.status);
    if (params.limit !== undefined) queryParams.append("limit", String(params.limit));
    if (params.offset !== undefined) queryParams.append("offset", String(params.offset));

    const queryString = queryParams.toString();
    const endpoint = `/v1/developer/syncers/runs${queryString ? `?${queryString}` : ""}`;

    return api.get<JobRunsListResponse>(endpoint);
  },

  /**
   * Get a single job run by ID
   */
  async getJobRun(runId: string): Promise<JobRunDetailResponse> {
    return api.get<JobRunDetailResponse>(`/v1/developer/syncers/runs/${runId}`);
  },

  /**
   * Trigger a new job
   */
  async triggerJob(
    jobType: JobType,
    triggeredBy?: string,
    params?: Record<string, unknown>,
  ): Promise<TriggerJobResponse> {
    return api.post<TriggerJobResponse>(`/v1/developer/syncers/trigger`, {
      jobType,
      triggered_by: triggeredBy,
      ...params,
    });
  },

  /**
   * Get the SSE endpoint URL for streaming logs
   */
  getLogsStreamUrl(runId: string): string {
    return api.url(`/v1/developer/syncers/runs/${runId}/logs/stream`);
  },
};
